import { Component, OnInit, computed, inject } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { RemoveTagEvent } from '@progress/kendo-angular-dropdowns'
import { forkJoin, map, Observable } from 'rxjs'

import {
  Enums,
  Environment,
  ExpandedHuntPackageRecord,
  ExpandedProfile,
  Hunt,
  Profile,
} from '@cyborg-monorepo/hunter/common'
import { DIALOG_DATA, DialogRef, DialogService } from '@hyperkit'

import { SessionService } from '@hunt/services'

import { isHuntPackageHuntCompatible } from '../../../../helpers/utils'
import { CompanyService } from '../../../shared/services/company.service'
import { ContentService } from '../../../shared/services/content.service'
import { EnvironmentService } from '../../../shared/services/environment.service'
import { HuntAPIService } from '../../services/api/hunt-api.service'

@Component({
  standalone: false,
  selector: 'edit-hunt-dialog',
  templateUrl: './edit-hunt-dialog.component.html',
  styleUrls: ['./edit-hunt-dialog.component.scss'],
})
export class EditHuntDialogComponent implements OnInit {
  dialogRef = inject(DialogRef)
  private formBuilder = inject(FormBuilder)
  private companyService = inject(CompanyService)
  dialogService = inject(DialogService)
  huntService = inject(HuntAPIService)
  contentService = inject(ContentService)
  environmentService = inject(EnvironmentService)
  hunt = inject<Hunt>(DIALOG_DATA, { optional: true })

  readonly session = inject(SessionService)
  readonly loaded = computed(
    () => !this.session.loading() && this.session.activeHunt() !== null,
  )
  public formGroup: FormGroup
  public priorities: Array<string> = []
  public assignees$: Observable<Array<Profile & ExpandedProfile>>
  public environments: Environment[]

  public ngOnInit(): void {
    this.environmentService.getAllEnvironments().subscribe((environments) => {
      this.environments = environments
    })

    this.priorities = Object.values(Enums.PriorityEnum)
    this.assignees$ = this.companyService.getUsers().pipe(
      map((users: Array<Profile>) => {
        return users.map((user: Profile) => {
          return {
            ...user,
            ...{ fullName: user.firstName + ' ' + user.lastName },
          }
        })
      }),
    )

    this.formGroup = this.formBuilder.group({
      name: [this.hunt.name, Validators.required],
      environments: new FormControl(this.hunt.environments, [
        Validators.required,
      ]),
      assignee: [this.hunt.assignee],
      priority: [this.hunt.priority],
    })
  }

  public removeTag(value: RemoveTagEvent): void {
    this.hunt.environments = this.hunt.environments.filter(
      function (environment) {
        return environment.id != value.dataItem.id
      },
    )

    this.hunt.huntPackages = this.hunt.huntPackages.map((huntPack) => {
      huntPack.analysis = huntPack.analysis.filter(function (environment) {
        return environment.id != value.dataItem.id
      })
      huntPack.originalAnalysis = huntPack.originalAnalysis.filter(
        function (environment) {
          return environment.id != value.dataItem.id
        },
      )
      return huntPack
    })
  }

  public onValueChange(value: Environment[]): void {
    if (value.length > this.hunt.environments.length) {
      const generatedHuntPackages: Observable<ExpandedHuntPackageRecord>[] =
        this.hunt.huntPackages
          .filter((huntPackage) => {
            return isHuntPackageHuntCompatible(huntPackage)
          })
          .map((huntPackage) => {
            const content = this.contentService
              .getHuntPackageContent(huntPackage.UUID, {
                environments: [value[value.length - 1].id],
              })
              .pipe(
                map((generatedContent) => {
                  huntPackage.analysis.push(
                    generatedContent[0]
                      ? generatedContent[0]
                      : value[value.length - 1],
                  )
                  huntPackage.originalAnalysis.push(
                    generatedContent[0]
                      ? generatedContent[0]
                      : value[value.length - 1],
                  )
                  return huntPackage
                }),
              )
            return content
          })
      forkJoin(generatedHuntPackages).subscribe((generatedHuntPacks) => {
        this.hunt.huntPackages = generatedHuntPacks
      })
    }
  }

  public onSubmit(): void {
    this.huntService
      .updateHunt(this.hunt.id, {
        ...this.formGroup.value,
        huntPackages: this.hunt.huntPackages,
      })
      .subscribe((hunt: Hunt) => {
        this.dialogRef.close(hunt)
      })
  }

  public onClose(): void {
    this.dialogRef.close()
  }
}
